import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { CheckWritingService } from 'src/app/services/checkWriting.service';
import { GeneralService } from 'src/app/services/general.service';
import { MarketInfoService } from 'src/app/services/marketInfo.service';
import { CommonCalculatorService } from 'src/app/Shared/commonCalculators.service';
import { EActionTypes, ECheckPrintPositionDefaults, ECountrycode, EPrintOrientation, EToaster, EUserSecurityFunctionID } from 'src/app/Shared/enum';
import { ICheckSetUpDetails, ICheckSignature } from 'src/app/Shared/Interfaces/ICheckWriting';
import { IMarketSettings } from 'src/app/Shared/Interfaces/IMarketSettings';
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';
import { ICommon, IUserPermissionHandler } from 'src/app/Shared/model/CommonModel';
import { ICountry, IStates } from 'src/app/Shared/model/GeneralModel';
import { ANGULAR_MODULES, OperationsComponent, SYNCFUSION_MODULES, TopBarComponent } from 'src/app/Shared/shared-imports';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { UserPermissionHandlerDirective } from 'src/app/Shared/userPermissionHandler.directive';
import Swal from 'sweetalert2';
import { WordReplacePipe } from "../../../Shared/word-replace.pipe";
import { Router } from '@angular/router';

@Component({
  selector: 'app-ops-check-setup',
  imports: [SYNCFUSION_MODULES, ANGULAR_MODULES, OperationsComponent, TopBarComponent, UserPermissionHandlerDirective, LoaderComponent, WordReplacePipe],
  providers: [CheckWritingService],
  standalone: true,
  templateUrl: './ops-check-setup.html',
  styleUrl: './ops-check-setup.scss'
})
export class OpsCheckSetup {

  isCollapsed = true;
  public loaderIcon: Promise<boolean>;
  private destroy$ = new Subject<void>();
  checkSetUpForm: FormGroup;
  defaultMarketData: IMarketSettings;
  states: IStates[] = [];
  bankStates: IStates[] = [];
  countries: ICountry[] = [];
  printOrientationList: ICommon[] = [];
  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.CheckSetUp, Action: EActionTypes.VIEW };
  createPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.CheckSetUp, Action: EActionTypes.CREATE };
  editPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.CheckSetUp, Action: EActionTypes.EDIT };
  public phoneNumberMask: String;
  tooltipPosition = 'TopCenter';

  signaturePreviewUrl: string = './assets/images/NoPhoto.svg';

  checkSignature: ICheckSignature = {
    CheckSetupId: 0,
    SignatureName: '',
    UploadedSignatureContent: null,
    DeleteFlag: false,
    PrintSignatureOnChecks: false
  };

  constructor(
    private formBuilder: FormBuilder,
    private checkWritingService: CheckWritingService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private marketInfoService: MarketInfoService,
    private generalService: GeneralService,
    private router: Router,
    private toastr: ToastrService,
    private commonCalculatorService: CommonCalculatorService
  ) {
    this.initializeCheckLayoutForm();
  }

  ngOnInit(): void {
    this.getCheckSetUpDetails();
    this.getMarketSettings();
    this.loadCountries();
    this.printOrientationList = this.checkWritingService.printOrientation;
    this.phoneNumberMask = this.sharedHTMLSettings.PhoneNumberMask
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeCheckLayoutForm(): void {
    this.checkSetUpForm = this.formBuilder.group({
      CheckSetupId: [0],
      MarketName: ['', Validators.required],
      Address: ['', Validators.required],
      Telephone: [''],
      ZipCode: ['', Validators.required],
      City: ['', Validators.required],
      State: [null, Validators.required],
      Country: [null, Validators.required],
      BankName: ['', Validators.required],
      BankAddress: ['', Validators.required],
      BankTelephone: [''],
      BankZipCode: ['', Validators.required],
      BankCity: ['', Validators.required],
      BankState: [null, Validators.required],
      BankCountry: [null, Validators.required],
      TransitCode: [''],
      CurrentCheckNumber: [null],
      CheckOrientation: [null, Validators.required],
      X1: [''],
      Y1: [''],
      X2: [''],
      Y2: [''],
      X3: [''],
      Y3: [''],
      RoutingNo: [''],
      AccountNo: [''],
      OnePlusOne: [false],
      OnePlusTwo: [false],
      PrintSignatureOnChecks: [false],
      CheckSignatureUrl: ['']
    });

    // this.checkSetUpForm.get('X1').disable();
    // this.checkSetUpForm.get('Y1').disable();
    this.checkSetUpForm.get('X2').disable();
    this.checkSetUpForm.get('Y2').disable();
    this.checkSetUpForm.get('X3').disable();
    this.checkSetUpForm.get('Y3').disable();

    this.checkSetUpForm.get('CheckOrientation')?.valueChanges.subscribe(value => {
      if (value == EPrintOrientation['2PerPage']) {
        this.checkSetUpForm.get('OnePlusOne').enable();
        this.checkSetUpForm.get('OnePlusTwo').disable();
        this.checkSetUpForm.get('X2').enable();
        this.checkSetUpForm.get('Y2').enable();
        this.checkSetUpForm.get('X3').disable();
        this.checkSetUpForm.get('Y3').disable();
        this.checkSetUpForm.patchValue({
          OnePlusOne: false,
          OnePlusTwo: false,
          X3: '',
          Y3: ''
        });
      } else if (value == EPrintOrientation['3PerPage']) {
        this.checkSetUpForm.get('OnePlusOne').disable();
        this.checkSetUpForm.get('OnePlusTwo').enable();
        this.checkSetUpForm.get('X2').enable();
        this.checkSetUpForm.get('Y2').enable();
        this.checkSetUpForm.get('X3').enable();
        this.checkSetUpForm.get('Y3').enable();
        this.checkSetUpForm.patchValue({
          OnePlusOne: false,
          OnePlusTwo: false
        });
      } else {
        if (this.checkSetUpForm.get('CheckOrientation').value == EPrintOrientation['1PerPage']) {
          this.checkSetUpForm.get('X2').disable();
          this.checkSetUpForm.get('Y2').disable();
          this.checkSetUpForm.get('X3').disable();
          this.checkSetUpForm.get('Y3').disable();
          this.checkSetUpForm.patchValue({
            X2: '',
            Y2: '',
            X3: '',
            Y3: ''
          });
        }
        this.checkSetUpForm.get('OnePlusOne').disable();
        this.checkSetUpForm.get('OnePlusTwo').disable();
        this.checkSetUpForm.get('OnePlusTwo').disable();
        this.checkSetUpForm.get('OnePlusTwo').disable();
        this.checkSetUpForm.get('OnePlusTwo').disable();
        this.checkSetUpForm.get('OnePlusTwo').disable();
        this.checkSetUpForm.get('OnePlusTwo').disable();
        this.checkSetUpForm.get('OnePlusTwo').disable();
        this.checkSetUpForm.patchValue({
          OnePlusOne: false,
          OnePlusTwo: false
        });
      }
    });
  }

  getCheckSetUpDetails() {
    this.checkWritingService.getCheckSetUpDetails().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: ICheckSetUpDetails) => {
        if (data) {
          this.checkSetUpForm.patchValue({
            CheckSetupId: data.CheckSetupId,
            MarketName: data.MarketName,
            Address: data.Address,
            Telephone: data.Telephone,
            ZipCode: data.ZipCode,
            City: data.City,
            State: data.State,
            Country: data.Country,
            BankName: data.BankName,
            BankAddress: data.BankAddress,
            BankCity: data.BankCity,
            BankState: data.State,
            BankCountry: data.BankCountry,
            BankTelephone: data.BankTelephone,
            BankZipCode: data.BankZipCode,
            TransitCode: data.TransitCode,
            CurrentCheckNumber: data.CurrentCheckNumber,
            CheckOrientation: data.CheckOrientation,
            X1: data.X1,
            Y1: data.Y1,
            X2: data.X2,
            Y2: data.Y2,
            X3: data.X3,
            Y3: data.Y3,
            RoutingNo: data.RoutingNo,
            AccountNo: data.AccountNo,
            OnePlusOne: data.OnePlusOne,
            OnePlusTwo: data.OnePlusTwo,
            PrintSignatureOnChecks: data.PrintSignatureOnChecks,
            CheckSignatureUrl: data.CheckSignatureUrl
          });

          this.signaturePreviewUrl =
            data.CheckSignatureUrl
              ? `${data.CheckSignatureUrl}?v=${Date.now()}`
              : './assets/images/NoPhoto.svg';

          let countryCode = data.Country;
          if (!countryCode) {
            countryCode = ECountrycode.US;
          }
          this.loadStates(countryCode, true);
          this.setZipCodeValidator(countryCode, true);

          let bankCountryCode = data.BankCountry;
          if (!bankCountryCode) {
            bankCountryCode = ECountrycode.US;
          }
          this.loadStates(bankCountryCode, false);
          this.setZipCodeValidator(bankCountryCode, false);
        }
      },
      error: err => { }
    });
  }

  setZipCodeValidator(countryCode: number, forMarket: boolean) {
    if (forMarket) {
      this.commonCalculatorService.setZipCodeValidator(countryCode, this.checkSetUpForm.get('ZipCode'));
    } else {
      this.commonCalculatorService.setZipCodeValidator(countryCode, this.checkSetUpForm.get('BankZipCode'));
    }
  }

  getMarketSettings(): void {
    this.marketInfoService.getMarketSettings().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: IMarketSettings) => {
        this.defaultMarketData = data;
      },
      error: (err) => { }
    });
  }

  loadStates(countryCode: number, forMarket: boolean): void {
    this.generalService.getStates(countryCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: IStates[]) => {
          if (forMarket) {
            this.states = res;
          } else {
            this.bankStates = res;
          }
        },
        error: (err) => {
          if (forMarket) {
            this.states = [];
            this.checkSetUpForm.patchValue({ State: '' });
          } else {
            this.bankStates = [];
            this.checkSetUpForm.patchValue({ BankState: '' });
          }
        }
      });
  }

  onCountryChange(event: Event, forMarket: boolean): void {
    const selectEl = event.target as HTMLSelectElement;
    const countryCode = Number(selectEl.value);
    if (countryCode) {
      this.loadStates(countryCode, forMarket);
      if (forMarket) {
        this.checkSetUpForm.patchValue({ State: null });
      } else {
        this.checkSetUpForm.patchValue({ BankState: null });
      }
      this.setZipCodeValidator(countryCode, forMarket);
    }
  }

  loadCountries(): void {
    this.generalService.getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ICountry[]) => {
          if (res && res.length > 0) {
            this.countries = res;
          } else {
            this.countries = [{ Code: ECountrycode.US, Name: 'US' }];
          }
        },
        error: (err) => {
          this.countries = [{ Code: ECountrycode.US, Name: 'US' }];
        }
      });
  }

  changeSetDefaultValue(event) {
    if (event.target.checked) {
      this.checkSetUpForm.patchValue({
        MarketName: this.defaultMarketData.MarketName,
        Address: this.defaultMarketData.MarketAddress,
        Telephone: this.defaultMarketData.Telephone,
        ZipCode: this.defaultMarketData.MarketZipCode,
        City: this.defaultMarketData.MarketCity,
        State: this.defaultMarketData.MarketStateCode,
        Country: this.defaultMarketData.MarketCountryCode
      });
      let countryCode = this.defaultMarketData.MarketCountryCode;
      if (!countryCode) {
        countryCode = ECountrycode.US;
      }
      this.loadStates(countryCode, true);
      this.setZipCodeValidator(countryCode, true);
    }
  }

  changeSetDefaultPrintPosition(checked) {
    if (checked) {
      if (this.checkSetUpForm.get('CheckOrientation').value == EPrintOrientation['1PerPage']) {
        this.checkSetUpForm.patchValue({
          X1: ECheckPrintPositionDefaults.X1,
          Y1: ECheckPrintPositionDefaults.Y1,
          X2: '',
          Y2: '',
          X3: '',
          Y3: ''
        });
      } else if (this.checkSetUpForm.get('CheckOrientation').value == EPrintOrientation['2PerPage']) {
        this.checkSetUpForm.patchValue({
          X1: ECheckPrintPositionDefaults.X1,
          Y1: ECheckPrintPositionDefaults.Y1,
          X2: ECheckPrintPositionDefaults.X2,
          Y2: ECheckPrintPositionDefaults.Y2,
          X3: '',
          Y3: ''
        });
      } else {
        this.checkSetUpForm.patchValue({
          X1: ECheckPrintPositionDefaults.X1,
          Y1: ECheckPrintPositionDefaults.Y1,
          X2: ECheckPrintPositionDefaults.X2,
          Y2: ECheckPrintPositionDefaults.Y2,
          X3: ECheckPrintPositionDefaults.X3,
          Y3: ECheckPrintPositionDefaults.Y3
        });
      }
    }
  }

  btnCancel() {
    this.router.navigate(['/dashboard']);
  }

  saveCheckSetUp(): void {
    if (this.checkSetUpForm.value.PrintSignatureOnChecks && this.isDefaultSignature) {
      Swal.fire({
        title: 'Validation',
        text: 'Please add Market Signature image and then save.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    Swal.fire({
      title: $localize`:@@Confirm:Confirm`,
      text: $localize`:@@ConfirmSavecheckSetUp:Are you sure you want to save this Check Set Up?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: $localize`:@@Yes:Yes`,
      cancelButtonText: $localize`:@@No:No`
    }).then((result) => {
      if (result.isConfirmed) {
        this.saveDatacheckSetUp();
      }
    });
  }

  saveDatacheckSetUp() {
    this.loaderIcon = Promise.resolve(true);
    this.commonCalculatorService.trimFormGroup(this.checkSetUpForm);
    let saveModel = this.checkSetUpForm.getRawValue();
    this.checkWritingService.saveCheckSetUpDetails(saveModel).pipe(takeUntil(this.destroy$)).subscribe({
      next: async (response) => {
        if (response.Success) {
          this.checkSetUpForm.patchValue({ CheckSetupId: response.Id });
          if (this.canSaveSignature) {
            this.checkSignature.CheckSetupId = response.Id;
            await this.saveCheckSignature(false);
          }
          this.loaderIcon = Promise.resolve(false);
          this.toastr.success(EToaster.MsgSuccessSave + $localize`:@@CheckSetUp:Check Set Up`, EToaster.TitleSuccess, this.sharedHTMLSettings.ToasterConfig);
        } else {
          this.toastr.error(EToaster.MsgErrorSave + $localize`:@@CheckSetUp:Check Set Up`, EToaster.TitleError, this.sharedHTMLSettings.ToasterConfig);
        }
      },
      error: (error) => {
        this.loaderIcon = Promise.resolve(false);
        this.toastr.error(EToaster.MsgErrorSave + $localize`:@@CheckSetUp:Check Set Up`, EToaster.TitleError, this.sharedHTMLSettings.ToasterConfig);
      },
    });
  }

  addSignatureClick(): void {
    const el = document.getElementById('signatureFileInput') as HTMLElement;
    if (el) {
      el.click();
    }
  }

  addSignature(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const allowedTypes = [
        'image/jpeg',
        'image/png'
      ];

      if (!allowedTypes.includes(file.type)) {
        alert('Only JPG and PNG files are allowed.');
        input.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        if (file.type === 'image/jpeg') {
          const base64Content =
            (reader.result as string).split(',')[1];

          this.signaturePreviewUrl =
            reader.result as string;

          this.checkSignature = {
            ...this.checkSignature,
            CheckSetupId:
              this.checkSetUpForm.value.CheckSetupId || 0,
            UploadedSignatureContent: base64Content,
            SignatureName: file.name,
            DeleteFlag: false,
            PrintSignatureOnChecks:
              this.checkSetUpForm.value.PrintSignatureOnChecks
          };
          input.value = '';
        }
        else {
          const img = new Image();
          img.onload = () => {
            const canvas =
              document.createElement('canvas');

            const ctx = canvas.getContext('2d')!;

            canvas.width = img.width;
            canvas.height = img.height;

            ctx.drawImage(img, 0, 0);

            const jpgDataUrl =
              canvas.toDataURL('image/jpeg', 0.9);

            const base64Content =
              jpgDataUrl.split(',')[1];

            this.signaturePreviewUrl =
              jpgDataUrl;

            this.checkSignature = {
              ...this.checkSignature,
              CheckSetupId:
                this.checkSetUpForm.value.CheckSetupId || 0,
              UploadedSignatureContent: base64Content,
              SignatureName:
                file.name.replace(/\.[^/.]+$/, '') + '.jpg',
              DeleteFlag: false,
              PrintSignatureOnChecks:
                this.checkSetUpForm.value.PrintSignatureOnChecks
            };
            input.value = '';
          };
          img.src = reader.result as string;
        }
      };
      reader.readAsDataURL(file);
    }
  }

  removeSignature(): void {
    this.checkSignature = {
      CheckSetupId: this.checkSetUpForm.value.CheckSetupId || 0,
      SignatureName: '',
      UploadedSignatureContent: null,
      DeleteFlag: true,
      PrintSignatureOnChecks: this.checkSetUpForm.value.PrintSignatureOnChecks
    };

    this.signaturePreviewUrl =
      './assets/images/NoPhoto.svg';

    const fileInput =
      document.getElementById(
        'signatureFileInput'
      ) as HTMLInputElement;

    if (fileInput) {
      fileInput.value = '';
    }
  }

  saveCheckSignature(showAlerts: boolean = true): Promise<void> {
    return new Promise((resolve) => {
      const payload: ICheckSignature = {
        CheckSetupId: this.checkSetUpForm.value.CheckSetupId || 0,
        SignatureName: this.checkSignature.SignatureName || '',
        UploadedSignatureContent: this.checkSignature.UploadedSignatureContent || null,
        DeleteFlag: this.checkSignature.DeleteFlag || false,
        PrintSignatureOnChecks: this.checkSetUpForm.value.PrintSignatureOnChecks
      };

      if (!payload.UploadedSignatureContent &&
        !payload.DeleteFlag) {
        resolve();
        return;
      }

      const actionText =
        payload.DeleteFlag ? 'delete' : 'add';

      const performSave = () => {
        this.checkWritingService
          .saveCheckSignature(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              if (res.SignatureUrl) {
                this.checkSignature.DeleteFlag = false;

                this.signaturePreviewUrl =
                  `${res.SignatureUrl}?v=${Date.now()}`;

                this.checkSetUpForm.patchValue({
                  CheckSignatureUrl: res.SignatureUrl
                });

                if (showAlerts) {
                  this.toastr.success(
                    'Check Signature saved successfully',
                    'Success',
                    this.sharedHTMLSettings.ToasterConfig
                  );
                }
              }
              else if (payload.DeleteFlag) {
                this.signaturePreviewUrl =
                  './assets/images/NoPhoto.svg';

                this.checkSetUpForm.patchValue({
                  CheckSignatureUrl: ''
                });

                if (showAlerts) {
                  this.toastr.success(
                    'Check Signature deleted successfully',
                    'Success',
                    this.sharedHTMLSettings.ToasterConfig
                  );
                }
              }

              this.checkSignature = {
                CheckSetupId: this.checkSetUpForm.value.CheckSetupId || 0,
                SignatureName: '',
                UploadedSignatureContent: null,
                DeleteFlag: false,
                PrintSignatureOnChecks: this.checkSetUpForm.value.PrintSignatureOnChecks
              };
              resolve();
            },

            error: () => {
              if (showAlerts) {
                this.toastr.error(
                  'Failed to save Check Signature',
                  'Error',
                  this.sharedHTMLSettings.ToasterConfig
                );
              }
              resolve();
            }
          });
      };

      if (!showAlerts) {
        performSave();
        return;
      }

      Swal.fire({
        title:
          `Are you sure you want to ${actionText} the Check Signature?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText:
          `Yes, ${actionText} it`,
        cancelButtonText: 'Cancel'
      }).then((result) => {

        if (result.isConfirmed) {
          performSave();
        }
        else {
          resolve();
        }
      });
    });
  }

  get canSaveSignature(): boolean {
    return !!this.checkSignature
      .UploadedSignatureContent
      || this.checkSignature.DeleteFlag;
  }

  get isDefaultSignature(): boolean {
    return this.signaturePreviewUrl
      === './assets/images/NoPhoto.svg';
  }

  get canRemoveSignature(): boolean {
    return !this.isDefaultSignature
      && !this.checkSignature.DeleteFlag;
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  onBeforeOpen(args: any): void {
    if (!args || !args.target || !args.target.dataset || !args.target.dataset.content) {
      args.cancel = true;
    }
  }
}
